/**
 * Hook para gestionar conversaciones sin proyectos
 * Reemplaza useProjectManagement después de eliminar el sistema de proyectos
 */

import { useState, useCallback } from 'react';
import { apiService, type Conversation } from '../services/api';
import type { ConversationUpdateRequest } from '../services/api/types/conversation';
import logger from '../utils/simpleLogger';

export function useConversationManagement(userId: string) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar conversaciones
  const loadConversations = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    setError(null);
    logger.info('💬 [Conversations] Loading conversations');

    try {
      const response = await apiService.getConversations();
      const loadedConversations = response.conversations || [];
      setConversations(loadedConversations);

      // If no conversations exist, create a default one
      if (loadedConversations.length === 0) {
        logger.info('💬 [Conversations] No conversations found, creating default conversation');
        try {
          const newConvResponse = await apiService.createConversation({
            name: 'Nueva conversación',
          });
          const newConversation = newConvResponse.conversation;
          setConversations([newConversation]);
          setCurrentConversation(newConversation);
          logger.info('✅ [Conversations] Default conversation created', {
            id: newConversation.id,
            name: newConversation.name,
          });
          return; // Exit early since we've set everything
        } catch (createErr) {
          logger.error('❌ [Conversations] Failed to create default conversation', createErr);
          // Continue with empty state
        }
      }

      // Auto-select first conversation if none is selected
      setCurrentConversation((prev) => {
        if (!prev && loadedConversations.length > 0) {
          logger.info('✅ [Conversations] Auto-selected first conversation', {
            id: loadedConversations[0].id,
            name: loadedConversations[0].name,
          });
          return loadedConversations[0];
        }
        return prev;
      });

      logger.info('✅ [Conversations] Loaded', { count: loadedConversations.length });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error loading conversations';

      const isNetworkError =
        err instanceof TypeError ||
        (err instanceof Error &&
          (err.message.includes('Failed to fetch') ||
            err.message.includes('NetworkError') ||
            err.message.includes('ERR_CONNECTION_REFUSED')));

      if (isNetworkError) {
        setError('Backend no disponible. ¿Está el servidor corriendo?');
        logger.info('ℹ️ [Conversations] Backend unavailable - waiting for server...');
      } else {
        setError(errorMessage);
        logger.error('❌ [Conversations] Failed to load', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Crear conversación
  const createConversation = useCallback(
    async (title: string): Promise<Conversation | null> => {
      if (!userId) return null;

      setIsLoading(true);
      setError(null);
      logger.info('💬 [Conversations] Creating conversation', { title });

      try {
        const response = await apiService.createConversation({ name: title });
        const newConversation = response.conversation;

        setConversations((prev) => [newConversation, ...prev]);
        setCurrentConversation(newConversation);

        logger.info('✅ [Conversations] Created', { id: newConversation.id, title });
        return newConversation;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error creating conversation';
        setError(errorMessage);
        logger.error('❌ [Conversations] Failed to create', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [userId]
  );

  // Seleccionar conversación
  const selectConversation = useCallback((conversation: Conversation | null) => {
    setCurrentConversation(conversation);
    logger.info('💬 [Conversations] Selected', {
      id: conversation?.id,
      name: conversation?.name,
    });
  }, []);

  // Actualizar conversación (PATCH)
  const updateConversation = useCallback(
    async (conversationId: string, updates: ConversationUpdateRequest): Promise<boolean> => {
      setError(null);
      logger.info('💬 [Conversations] Updating', { conversationId, updates });

      try {
        const response = await apiService.updateConversation(conversationId, updates);
        const updatedConversation = response.conversation;

        setConversations((prev) =>
          prev.map((c) => (c.id === conversationId ? updatedConversation : c))
        );

        if (currentConversation?.id === conversationId) {
          setCurrentConversation(updatedConversation);
        }

        logger.info('✅ [Conversations] Updated', { id: conversationId });
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error updating conversation';
        setError(errorMessage);
        logger.error('❌ [Conversations] Failed to update', err);
        return false;
      }
    },
    [currentConversation]
  );

  // Fijar/desfijar conversación
  const pinConversation = useCallback(
    async (conversationId: string, pinned: boolean): Promise<boolean> => {
      return updateConversation(conversationId, { is_pinned: pinned });
    },
    [updateConversation]
  );

  // Archivar conversación
  const archiveConversation = useCallback(
    async (conversationId: string): Promise<boolean> => {
      const success = await updateConversation(conversationId, { is_active: false });
      if (success) {
        setConversations((prev) => prev.filter((c) => c.id !== conversationId));
        if (currentConversation?.id === conversationId) {
          setCurrentConversation(null);
        }
      }
      return success;
    },
    [updateConversation, currentConversation]
  );

  // Duplicar conversación
  const duplicateConversation = useCallback(
    async (conversationId: string): Promise<Conversation | null> => {
      setError(null);
      logger.info('💬 [Conversations] Duplicating', { conversationId });

      try {
        const response = await apiService.duplicateConversation(conversationId);
        const newConversation = response.conversation;
        setConversations((prev) => [newConversation, ...prev]);
        setCurrentConversation(newConversation);
        logger.info('✅ [Conversations] Duplicated', { newId: newConversation.id });
        return newConversation;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error duplicating conversation';
        setError(errorMessage);
        logger.error('❌ [Conversations] Failed to duplicate', err);
        return null;
      }
    },
    []
  );

  // Eliminar conversación
  const deleteConversation = useCallback(
    async (conversationId: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);
      logger.info('💬 [Conversations] Deleting', { conversationId });

      try {
        await apiService.deleteConversation(conversationId);

        setConversations((prev) => prev.filter((c) => c.id !== conversationId));

        if (currentConversation?.id === conversationId) {
          setCurrentConversation(null);
        }

        logger.info('✅ [Conversations] Deleted', { id: conversationId });
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error deleting conversation';
        setError(errorMessage);
        logger.error('❌ [Conversations] Failed to delete', err);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [currentConversation]
  );

  return {
    conversations,
    currentConversation,
    isLoading,
    error,
    loadConversations,
    createConversation,
    selectConversation,
    updateConversation,
    deleteConversation,
    pinConversation,
    archiveConversation,
    duplicateConversation,
  };
}
